import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authService } from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Get account_type from user for redirect logic
  const getAccountType = useCallback((userData) => {
    if (!userData) return 'user';
    // Prefer new account_type field, fallback to role mapping
    if (userData.account_type) return userData.account_type;
    
    // Legacy role mapping
    const role = userData.role || userData.user_role;
    const roleMap = {
      'customer': 'user',
      'client_owner': 'business_owner',
      'client_staff': 'business_staff',
      'kiosk_owner': 'business_owner',
      'kiosk_staff': 'business_staff',
      'admin': 'admin',
      'beach_pay_admin': 'admin',
      'brand': 'admin',
      'city_hall': 'admin',
    };
    return roleMap[role] || 'user';
  }, []);

  const login = useCallback(async (email, password) => {
    const userData = await authService.login(email, password);
    // Add account_type to user for easy access in components
    const accountType = getAccountType(userData);
    const userWithAccountType = { ...userData, account_type: accountType };
    setUser(userWithAccountType);
    return userWithAccountType;
  }, [getAccountType]);

  const register = useCallback(async (data) => {
    const result = await authService.register(data);
    return result;
  }, []);

  const logout = useCallback(() => {
    authService.logout();
    setUser(null);
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        const userData = authService.getCurrentUser();
        if (userData) {
          const accountType = getAccountType(userData);
          setUser({ ...userData, account_type: accountType });
        }
      }
      setLoading(false);
    };
    initAuth();
  }, [getAccountType]);

  return (
    <AuthContext.Provider value={{ user, login, register, logout, loading, isAuthenticated: !!user }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
